import React, { useRef } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { useDrag, useDrop } from "hooks";

import { formatBytes } from "utils/common";

interface SortableFileRowProps {
  id: string;
  file: File;
  index: number;
  removeFile: (index: number) => void;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
}

const SortableFileRow: React.FC<SortableFileRowProps> = ({
  id,
  file,
  index,
  moveRow,
  removeFile
}) => {
  const theme = useTheme();

  const ref = useRef<HTMLDivElement>(null);
  const { handlerId, drop } = useDrop({
    ref,
    index,
    moveRow,
    moveType: "fileRow"
  });
  const { isDragging, drag } = useDrag({ id, index, moveType: "fileRow" });
  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <li>
      <Box
        ref={ref}
        style={{ opacity }}
        data-handler-id={handlerId}
        key={index}
        sx={{
          position: "relative",
          backgroundColor: `${theme.palette.info.light}10`,
          borderRadius: 1.5,
          p: 0.5,
          "&:hover": {
            cursor: "move"
          }
        }}
      >
        <Box display="flex" sx={{ alignItems: "center" }}>
          <Box
            display="flex"
            sx={{
              height: "24px",
              width: "24px",
              ml: 1
            }}
          >
            <InsertDriveFileIcon />
          </Box>

          <Box sx={{ ml: 1 }}>
            <Typography>{file.name}</Typography>
            <Typography variant="body2">{formatBytes(file.size, 2)}</Typography>
          </Box>
        </Box>
        <IconButton
          onClick={() => removeFile(index)}
          sx={{
            color: theme.palette.error.main,
            position: "absolute",
            right: "1rem",
            top: "50%",
            transform: "translateY(-50%)"
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </li>
  );
};

export default SortableFileRow;
